import { useState, useEffect, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import { useIsAdmin } from '@/hooks/useIsAdmin';
import { useAuthorSeeds, AuthorSeed } from '@/hooks/useAuthorSeeds';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Upload, Plus, Search, Pause, Play, Trash2, FileText, Users, TrendingUp, Zap, BookOpen } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export default function AdminAuthorSeeds() {
  const { isAdmin, loading: adminLoading } = useIsAdmin();
  const {
    seeds,
    genres,
    total,
    loading,
    stats,
    fetchSeeds,
    uploadCSV,
    updateSeed,
    deleteSeed,
    addSeed,
    fetchStats,
  } = useAuthorSeeds();

  const [filters, setFilters] = useState({
    status: 'all' as 'all' | 'active' | 'paused',
    genre: 'all',
    tier: 'all',
    search: '',
  });

  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [csvPreview, setCsvPreview] = useState<string[][]>([]);
  const [uploading, setUploading] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [pastePreview, setPastePreview] = useState<string[][]>([]);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<{
    fetched?: number;
    filtered?: number;
    duplicates?: number;
    imported?: number;
    errors?: string[];
    authorsProcessed?: string[];
    filteredSamples?: { title: string; reason: string; publisher?: string; pages?: number }[];
  } | null>(null);
  const [showFiltered, setShowFiltered] = useState(false);
  const [importSettings, setImportSettings] = useState({
    minPages: 0,
    requireCover: false,
    ignoreMaxWorks: true,  // Fetch ALL books per author, not limited by max_works
    forceAll: false,        // Re-run all authors regardless of last_imported_at
    batchSize: 10,          // Authors per run (smaller = faster, avoids timeout)
  });
  const { toast } = useToast();
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newSeed, setNewSeed] = useState({
    name: '',
    primary_genre: '',
    priority_tier: 2,
    max_works: 10,
    allow_backlist: true,
    notes: '',
  });

  useEffect(() => {
    if (isAdmin) {
      fetchSeeds({
        status: filters.status === 'all' ? undefined : filters.status,
        genre: filters.genre === 'all' ? undefined : filters.genre,
        tier: filters.tier === 'all' ? undefined : parseInt(filters.tier),
        search: filters.search || undefined,
      });
      fetchStats();
    }
  }, [isAdmin, filters, fetchSeeds, fetchStats]);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setCsvFile(file);
      const reader = new FileReader();
      reader.onload = (event) => {
        const text = event.target?.result as string;
        const lines = text.split('\n').slice(0, 11).map(line => 
          line.split(',').map(cell => cell.trim().replace(/^"|"$/g, ''))
        );
        setCsvPreview(lines);
      };
      reader.readAsText(file);
    }
  }, []);

  const handleUpload = useCallback(async () => {
    if (!csvFile) return;
    setUploading(true);
    try {
      const text = await csvFile.text();
      await uploadCSV(text, csvFile.name);
      setCsvFile(null);
      setCsvPreview([]);
      fetchSeeds();
      fetchStats();
    } finally {
      setUploading(false);
    }
  }, [csvFile, uploadCSV, fetchSeeds, fetchStats]);

  const handlePasteChange = useCallback((text: string) => {
    setPasteText(text);
    if (text.trim()) {
      const lines = text.split('\n').slice(0, 11).map(line => 
        line.split(',').map(cell => cell.trim().replace(/^"|"$/g, ''))
      );
      setPastePreview(lines);
    } else {
      setPastePreview([]);
    }
  }, []);

  const handlePasteUpload = useCallback(async () => {
    if (!pasteText.trim()) return;
    setUploading(true);
    try {
      await uploadCSV(pasteText, 'pasted-data.csv');
      setPasteText('');
      setPastePreview([]);
      fetchSeeds();
      fetchStats();
    } finally {
      setUploading(false);
    }
  }, [pasteText, uploadCSV, fetchSeeds, fetchStats]);

  const handleAddSeed = useCallback(async () => {
    await addSeed({
      name: newSeed.name,
      primary_genre: newSeed.primary_genre || null,
      priority_tier: newSeed.priority_tier,
      max_works: newSeed.max_works,
      allow_backlist: newSeed.allow_backlist,
      notes: newSeed.notes || null,
      status: 'active',
    });
    setAddDialogOpen(false);
    setNewSeed({
      name: '',
      primary_genre: '',
      priority_tier: 2,
      max_works: 10,
      allow_backlist: true,
      notes: '',
    });
  }, [addSeed, newSeed]);

  const handleToggleStatus = useCallback(async (seed: AuthorSeed) => {
    await updateSeed(seed.id, { status: seed.status === 'active' ? 'paused' : 'active' });
  }, [updateSeed]);

  const handleUpdateField = useCallback(async (id: string, field: keyof AuthorSeed, value: unknown) => {
    await updateSeed(id, { [field]: value });
  }, [updateSeed]);

  const handleRunImport = useCallback(async () => {
    setImporting(true);
    setImportResult(null);
    
    try {
      // Create timeout promise for 10 minutes (large imports can take a while)
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('IMPORT_TIMEOUT')), 10 * 60 * 1000);
      });
      
      const invokePromise = supabase.functions.invoke('import-isbndb-books', {
        body: {
          action: 'import-from-seeds',
          minPages: importSettings.minPages,
          requireCover: importSettings.requireCover,
          ignoreMaxWorks: importSettings.ignoreMaxWorks,
          onlyNew: !importSettings.forceAll,
          batchSize: importSettings.batchSize,
        },
      });
      
      const { data, error } = await Promise.race([invokePromise, timeoutPromise]);

      if (error) throw error;

      setImportResult(data);
      toast({
        title: 'Import Complete',
        description: `Imported ${data.imported} books from ${data.authorsProcessed?.length || importSettings.batchSize} authors. Run again to process more.`,
      });
      fetchStats();
    } catch (error) {
      console.error('Import error:', error);
      
      // Check if it's a timeout or network error
      const isTimeout = error instanceof Error && (
        error.message === 'IMPORT_TIMEOUT' ||
        error.message.includes('Failed to fetch') ||
        error.message.includes('Failed to send a request')
      );
      
      toast({
        variant: 'destructive',
        title: 'Import Failed',
        description: isTimeout 
          ? 'The import is taking too long. It may still be running in the background - check the edge function logs.' 
          : (error instanceof Error ? error.message : 'Unknown error'),
      });
    } finally {
      setImporting(false);
    }
  }, [stats, toast, fetchStats]);

  if (adminLoading) {
    return <div className="flex items-center justify-center min-h-screen">Loading...</div>;
  }

  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="container mx-auto py-8 px-4 max-w-7xl">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold">Author Seeds</h1>
          <p className="text-muted-foreground mt-1">
            Manage curated author list for book seeding
          </p>
        </div>
        <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Add Author
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Author Seed</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="name">Author Name *</Label>
                <Input
                  id="name"
                  value={newSeed.name}
                  onChange={(e) => setNewSeed({ ...newSeed, name: e.target.value })}
                  placeholder="Stephen King"
                />
              </div>
              <div>
                <Label htmlFor="genre">Primary Genre</Label>
                <Input
                  id="genre"
                  value={newSeed.primary_genre}
                  onChange={(e) => setNewSeed({ ...newSeed, primary_genre: e.target.value })}
                  placeholder="Horror"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="tier">Priority Tier</Label>
                  <Select
                    value={String(newSeed.priority_tier)}
                    onValueChange={(v) => setNewSeed({ ...newSeed, priority_tier: parseInt(v) })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1 - High</SelectItem>
                      <SelectItem value="2">2 - Medium</SelectItem>
                      <SelectItem value="3">3 - Low</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="max">Max Works</Label>
                  <Input
                    id="max"
                    type="number"
                    min={1}
                    max={100}
                    value={newSeed.max_works}
                    onChange={(e) => setNewSeed({ ...newSeed, max_works: parseInt(e.target.value) || 10 })}
                  />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={newSeed.allow_backlist}
                  onCheckedChange={(c) => setNewSeed({ ...newSeed, allow_backlist: c })}
                />
                <Label>Allow Backlist</Label>
              </div>
              <div>
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  value={newSeed.notes}
                  onChange={(e) => setNewSeed({ ...newSeed, notes: e.target.value })}
                  placeholder="Optional notes..."
                />
              </div>
              <Button onClick={handleAddSeed} disabled={!newSeed.name.trim()} className="w-full">
                Add Author Seed
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <Users className="h-8 w-8 text-primary" />
                <div>
                  <p className="text-2xl font-bold">{stats.total}</p>
                  <p className="text-sm text-muted-foreground">Total Authors</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <Play className="h-8 w-8 text-green-500" />
                <div>
                  <p className="text-2xl font-bold">{stats.byStatus.active}</p>
                  <p className="text-sm text-muted-foreground">Active</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <TrendingUp className="h-8 w-8 text-yellow-500" />
                <div>
                  <p className="text-2xl font-bold">{stats.byTier[1]}</p>
                  <p className="text-sm text-muted-foreground">Tier 1 (High)</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <FileText className="h-8 w-8 text-blue-500" />
                <div>
                  <p className="text-2xl font-bold">{stats.recentUploads?.length || 0}</p>
                  <p className="text-sm text-muted-foreground">Recent Uploads</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* CSV Upload / Paste */}
      <Card className="mb-8">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" />
            Import Authors
          </CardTitle>
          <CardDescription>
            Upload a CSV file or paste CSV data. Required column: name. Optional: primary_genre, priority_tier, max_works, allow_backlist, notes
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-6">
            {/* File Upload */}
            <div className="space-y-4">
              <Label className="text-sm font-medium">Option 1: Upload CSV File</Label>
              <div className="flex items-center gap-4">
                <Input
                  type="file"
                  accept=".csv"
                  onChange={handleFileChange}
                  className="max-w-xs"
                />
                <Button onClick={handleUpload} disabled={!csvFile || uploading}>
                  {uploading ? 'Uploading...' : 'Upload'}
                </Button>
              </div>

              {csvPreview.length > 0 && (
                <div className="mt-4">
                  <p className="text-sm font-medium mb-2">Preview (first 10 rows):</p>
                  <div className="border rounded overflow-auto max-h-64">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          {csvPreview[0]?.map((header, i) => (
                            <TableHead key={i} className="whitespace-nowrap">{header}</TableHead>
                          ))}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {csvPreview.slice(1).map((row, rowIndex) => (
                          <TableRow key={rowIndex}>
                            {row.map((cell, cellIndex) => (
                              <TableCell key={cellIndex} className="whitespace-nowrap">{cell}</TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </div>

            {/* Paste Data */}
            <div className="space-y-4">
              <Label className="text-sm font-medium">Option 2: Paste CSV Data</Label>
              <Textarea
                placeholder={`name,primary_genre,priority_tier
Stephen King,Horror,1
Brandon Sanderson,Fantasy,1
Colleen Hoover,Romance,2`}
                value={pasteText}
                onChange={(e) => handlePasteChange(e.target.value)}
                className="min-h-[120px] font-mono text-sm"
              />
              <Button onClick={handlePasteUpload} disabled={!pasteText.trim() || uploading}>
                {uploading ? 'Uploading...' : 'Import Pasted Data'}
              </Button>

              {pastePreview.length > 0 && (
                <div className="mt-4">
                  <p className="text-sm font-medium mb-2">Preview (first 10 rows):</p>
                  <div className="border rounded overflow-auto max-h-64">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          {pastePreview[0]?.map((header, i) => (
                            <TableHead key={i} className="whitespace-nowrap">{header}</TableHead>
                          ))}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {pastePreview.slice(1).map((row, rowIndex) => (
                          <TableRow key={rowIndex}>
                            {row.map((cell, cellIndex) => (
                              <TableCell key={cellIndex} className="whitespace-nowrap">{cell}</TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Run ISBNdb Import */}
      <Card className="mb-8 border-primary/50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5 text-primary" />
            Run ISBNdb Import
          </CardTitle>
          <CardDescription>
            Fetch books from ISBNdb for all active author seeds. This will search ISBNdb for each author and import their books.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Filter Settings */}
          <div className="flex flex-wrap items-center gap-4 mb-4 p-3 bg-muted/50 rounded-lg">
            <div className="flex items-center gap-2">
              <Label htmlFor="minPages" className="text-sm whitespace-nowrap">Min Pages:</Label>
              <Input
                id="minPages"
                type="number"
                min={0}
                max={200}
                value={importSettings.minPages}
                onChange={(e) => setImportSettings(prev => ({ ...prev, minPages: parseInt(e.target.value) || 0 }))}
                className="w-20 h-8"
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch
                id="requireCover"
                checked={importSettings.requireCover}
                onCheckedChange={(c) => setImportSettings(prev => ({ ...prev, requireCover: c }))}
              />
              <Label htmlFor="requireCover" className="text-sm">Require Cover</Label>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                id="ignoreMaxWorks"
                checked={importSettings.ignoreMaxWorks}
                onCheckedChange={(c) => setImportSettings(prev => ({ ...prev, ignoreMaxWorks: c }))}
              />
              <Label htmlFor="ignoreMaxWorks" className="text-sm">Fetch ALL books (ignore max_works limit)</Label>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                id="forceAll"
                checked={importSettings.forceAll}
                onCheckedChange={(c) => setImportSettings(prev => ({ ...prev, forceAll: c }))}
              />
              <Label htmlFor="forceAll" className="text-sm">Re-run ALL authors (ignore 24h cooldown)</Label>
            </div>
            <div className="flex items-center gap-2">
              <Label htmlFor="batchSize" className="text-sm whitespace-nowrap">Authors per run:</Label>
              <Select
                value={String(importSettings.batchSize)}
                onValueChange={(v) => setImportSettings(prev => ({ ...prev, batchSize: parseInt(v) }))}
              >
                <SelectTrigger className="w-24 h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="5">5</SelectItem>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="20">20</SelectItem>
                  <SelectItem value="30">30</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                </SelectContent>
              </Select>
              <span className="text-xs text-muted-foreground">(smaller = faster, avoids timeout)</span>
            </div>
          </div>
          
          {/* Filtering Criteria Explanation */}
          <details className="mb-4 text-sm">
            <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
              View all filtering criteria applied
            </summary>
            <div className="mt-2 p-3 bg-muted/30 rounded-lg space-y-2 text-muted-foreground">
              <p><strong>Junk Title Patterns:</strong> study guide, cliff notes, sparknotes, shmoop, reader's guide, teacher's guide, workbook, student edition, summary & analysis, quick study, for dummies, idiot's guide, test prep, coloring book, blank journal, large print edition, box set</p>
              <p><strong>Junk Publishers:</strong> cliffsnotes, sparknotes, shmoop, barron's, kaplan, princeton review, independently published, createspace, lulu, xlibris, authorhouse, iuniverse</p>
              <p><strong>Other Filters:</strong> Non-English language, no ISBN, page count outside min-max range, missing cover (if required), missing synopsis (if required)</p>
            </div>
          </details>
          
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
            <Button 
              onClick={handleRunImport} 
              disabled={importing || !stats?.byStatus.active}
              size="lg"
              className="gap-2"
            >
              {importing ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-current border-t-transparent" />
                  Importing...
                </>
              ) : (
                <>
                  <BookOpen className="h-4 w-4" />
                  Import Books ({Math.min(importSettings.batchSize, stats?.byStatus.active || 0)} of {stats?.byStatus.active || 0} authors)
                </>
              )}
            </Button>
            
            {importResult && (
              <div className="flex flex-wrap gap-2 text-sm">
                <Badge variant="outline">Fetched: {importResult.fetched}</Badge>
                <Badge variant="outline">Filtered: {importResult.filtered}</Badge>
                <Badge variant="outline">Duplicates: {importResult.duplicates}</Badge>
                <Badge variant="default" className="bg-green-600">Imported: {importResult.imported}</Badge>
                {importResult.errors && importResult.errors.length > 0 && (
                  <Badge variant="destructive">Errors: {importResult.errors.length}</Badge>
                )}
              </div>
            )}
          </div>
          
          {importResult?.errors && importResult.errors.length > 0 && (
            <div className="mt-4 p-3 bg-destructive/10 rounded-lg text-sm">
              <p className="font-medium text-destructive mb-2">Errors:</p>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                {importResult.errors.slice(0, 10).map((err, i) => (
                  <li key={i}>{err}</li>
                ))}
                {importResult.errors.length > 10 && (
                  <li>...and {importResult.errors.length - 10} more</li>
                )}
              </ul>
            </div>
          )}
          
          {importResult?.authorsProcessed && importResult.authorsProcessed.length > 0 && (
            <div className="mt-4 p-3 bg-muted rounded-lg text-sm">
              <p className="font-medium mb-2">Authors Processed ({importResult.authorsProcessed.length}):</p>
              <p className="text-muted-foreground">
                {importResult.authorsProcessed.slice(0, 20).join(', ')}
                {importResult.authorsProcessed.length > 20 && ` ...and ${importResult.authorsProcessed.length - 20} more`}
              </p>
            </div>
          )}
          
          {importResult?.filteredSamples && importResult.filteredSamples.length > 0 && (
            <div className="mt-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowFiltered(!showFiltered)}
                className="mb-2"
              >
                {showFiltered ? 'Hide' : 'Show'} Filtered Books ({importResult.filtered})
              </Button>
              
              {showFiltered && (
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Title</TableHead>
                        <TableHead>Reason</TableHead>
                        <TableHead>Publisher</TableHead>
                        <TableHead>Pages</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {importResult.filteredSamples.map((sample, i) => (
                        <TableRow key={i}>
                          <TableCell className="font-medium max-w-xs truncate">{sample.title}</TableCell>
                          <TableCell className="text-muted-foreground">{sample.reason}</TableCell>
                          <TableCell className="text-muted-foreground">{sample.publisher || '-'}</TableCell>
                          <TableCell className="text-muted-foreground">{sample.pages || '-'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {(importResult.filtered || 0) > importResult.filteredSamples.length && (
                    <p className="p-2 text-sm text-muted-foreground text-center border-t">
                      Showing {importResult.filteredSamples.length} of {importResult.filtered} filtered books
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Search className="h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search authors..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                className="w-48"
              />
            </div>
            <Select
              value={filters.status}
              onValueChange={(v) => setFilters({ ...filters, status: v as 'all' | 'active' | 'paused' })}
            >
              <SelectTrigger className="w-32">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="paused">Paused</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={filters.genre}
              onValueChange={(v) => setFilters({ ...filters, genre: v })}
            >
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Genre" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Genres</SelectItem>
                {genres.map((g) => (
                  <SelectItem key={g} value={g}>{g}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={filters.tier}
              onValueChange={(v) => setFilters({ ...filters, tier: v })}
            >
              <SelectTrigger className="w-32">
                <SelectValue placeholder="Tier" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Tiers</SelectItem>
                <SelectItem value="1">Tier 1</SelectItem>
                <SelectItem value="2">Tier 2</SelectItem>
                <SelectItem value="3">Tier 3</SelectItem>
              </SelectContent>
            </Select>
            <span className="text-sm text-muted-foreground ml-auto">
              {total} author{total !== 1 ? 's' : ''}
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Seeds Table */}
      <Card>
        <CardContent className="pt-6">
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Loading...</div>
          ) : seeds.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No author seeds found. Upload a CSV or add authors manually.
            </div>
          ) : (
            <div className="overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Genre</TableHead>
                    <TableHead>Tier</TableHead>
                    <TableHead>Max Works</TableHead>
                    <TableHead>Backlist</TableHead>
                    <TableHead>Last Imported</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {seeds.map((seed) => (
                    <TableRow key={seed.id}>
                      <TableCell className="font-medium">{seed.name}</TableCell>
                      <TableCell>
                        <Input
                          value={seed.primary_genre || ''}
                          onChange={(e) => handleUpdateField(seed.id, 'primary_genre', e.target.value || null)}
                          className="w-28 h-8"
                          placeholder="—"
                        />
                      </TableCell>
                      <TableCell>
                        <Select
                          value={String(seed.priority_tier)}
                          onValueChange={(v) => handleUpdateField(seed.id, 'priority_tier', parseInt(v))}
                        >
                          <SelectTrigger className="w-20 h-8">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="1">1</SelectItem>
                            <SelectItem value="2">2</SelectItem>
                            <SelectItem value="3">3</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          min={1}
                          max={100}
                          value={seed.max_works}
                          onChange={(e) => handleUpdateField(seed.id, 'max_works', parseInt(e.target.value) || 10)}
                          className="w-16 h-8"
                        />
                      </TableCell>
                      <TableCell>
                        <Switch
                          checked={seed.allow_backlist}
                          onCheckedChange={(c) => handleUpdateField(seed.id, 'allow_backlist', c)}
                        />
                      </TableCell>
                      <TableCell>
                        {seed.last_imported_at ? (
                          <span className="text-xs text-muted-foreground">
                            {new Date(seed.last_imported_at).toLocaleDateString()}
                          </span>
                        ) : (
                          <Badge variant="outline" className="text-xs">Never</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant={seed.status === 'active' ? 'default' : 'secondary'}>
                          {seed.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleToggleStatus(seed)}
                            title={seed.status === 'active' ? 'Pause' : 'Activate'}
                          >
                            {seed.status === 'active' ? (
                              <Pause className="h-4 w-4" />
                            ) : (
                              <Play className="h-4 w-4" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => deleteSeed(seed.id, true)}
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
